// src/app/utils/signal-items.ts
//
// Strength/weakness entries arrive from the backend either as structured
// {label, description} objects (new runs: the prompt contract requires the
// pair, and the backend normalizes at the parse boundary) or as bare
// sentence strings (legacy stored runs). This is the single frontend
// adapter: everything downstream renders {label, description} and never
// re-derives structure on its own.
//
// Mirror of normalize_signal_item in backend monitoring_export_common.py.

import { stripCitations } from "@/app/utils/strip-citations";

export type SignalEntry =
  | string
  | { label?: string | null; description?: string | null };

export interface SignalItem {
  label: string;
  description?: string;
}

// House rule: no em or en dashes anywhere, including inside model-supplied
// text. Numeric ranges keep their meaning as hyphens (5 to 15% stays 5-15%);
// clause dashes become commas. Punctuation-only edit, never words.
const DASH_RANGE = /(?<=\d)\s*[—–]\s*(?=\d)/g;
const DASH_CLAUSE = /\s*[—–]\s*/g;

function cleanDashes(text: string): string {
  return text.replace(DASH_RANGE, "-").replace(DASH_CLAUSE, ", ");
}

// An item is only as rich as the model actually made it: a structured
// {label, description} pair keeps both fields; a bare string stays a bare
// label with NO derived description. Thin data renders thin (the "Also
// noted" tier is the fallback for it), never dressed up to match the rich
// format.
export function toSignalItem(entry: SignalEntry): SignalItem | null {
  if (entry && typeof entry === "object") {
    const label = cleanDashes(stripCitations(String(entry.label ?? ""))).trim();
    const desc = entry.description
      ? cleanDashes(stripCitations(String(entry.description))).trim()
      : "";
    if (label) {
      return desc
        ? { label: label.replace(/[.;,]$/, ""), description: desc }
        : { label: label.replace(/[.;,]$/, "") };
    }
    if (desc) return { label: desc.replace(/[.;,]$/, "") };
    return null;
  }
  const text = cleanDashes(stripCitations(String(entry ?? ""))).trim();
  if (!text) return null;
  return { label: text.replace(/[.;,]$/, "") };
}

export function toSignalItems(entries: SignalEntry[] | undefined | null): SignalItem[] {
  const out: SignalItem[] = [];
  const seen = new Set<string>();
  for (const e of entries || []) {
    const item = toSignalItem(e);
    if (!item) continue;
    const key = item.label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

export function signalLabels(entries: SignalEntry[] | undefined | null): string[] {
  return toSignalItems(entries).map((i) => i.label);
}
